import React, { Suspense, useEffect } from 'react';
import { ErrorBoundary } from '@funtech-int/rival-react';
import { TarifficatorPurchaseSuccessWidgetEvent, usePaymentPlace } from '@funtech-int/yango-shared';

import { useAutoOpenPaymentWidgetParams } from '../../hooks/useAutoOpenPaymentWidgetParams';
import { useLoginUrl } from '../../hooks/useLoginUrl';
import { getAutoOpenWidgetUrl } from '../../lib/getAutoOpenWidgetUrl';
import { useSubscriptionRefetch } from '../../hooks/useSubscriptionRefetch';
import { useOfferWebAnalytics } from '../../hooks/useOfferWebAnalytics';
import { usePaymentWidgetModal } from '../../hooks/usePopups';

interface WidgetOpenerProps {
    billingFeatureName: string | undefined;
    target: string;
    onDismiss: VoidFunction;
    onSuccessPurchase?: VoidFunction;
}

const WidgetOpener = ({
    billingFeatureName,
    target,
    onDismiss,
    onSuccessPurchase,
}: WidgetOpenerProps) => {
    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();

    const paymentPlace = usePaymentPlace({
        target: target ?? '',
        features: [billingFeatureName ?? ''],
        extraParams: {
            customProperties: { billingFeatureName },
        },
    });

    const offer = paymentPlace?.offers?.[0] ?? null;

    useEffect(() => {
        const tariffOfferName = offer?.catalogCompositeOffer?.tariffOffer?.name;
        const optionOfferNames = offer?.catalogCompositeOffer?.optionOffers?.[0]?.name;

        const isEmptyOffer = !tariffOfferName && !optionOfferNames;

        if (isEmptyOffer) {
            onDismiss();
        }
    }, [onDismiss, offer]);

    const offerLoginUrl = useLoginUrl(getAutoOpenWidgetUrl(offer));
    const offerWebAnalytics = useOfferWebAnalytics(offer);

    const paymentWidget = usePaymentWidgetModal();

    useEffect(() => {
        function onSuccess(data: TarifficatorPurchaseSuccessWidgetEvent) {
            subscriptionRefetch();
            offerWebAnalytics.purchaseSucceeded(data);
            onSuccessPurchase?.();
        }

        if (offer) {
            paymentWidget.open(paymentPlace, offer, {
                onClose: onDismiss,
                onPurchaseSuccess: onSuccess,
                onNeedAuth: () => window.location.assign(offerLoginUrl),
            });
        }
    }, [
        onDismiss,
        offer,
        offerLoginUrl,
        offerWebAnalytics,
        onSuccessPurchase,
        paymentWidget,
        paymentPlace,
        subscriptionRefetch,
    ]);

    return null;
};

interface AutoOpenPaymentWidgetProps {
    onSuccessPurchase?: VoidFunction;
}

export const AutoOpenPaymentWidget = ({ onSuccessPurchase }: AutoOpenPaymentWidgetProps) => {
    const { billingFeatureName, target, shouldOpenWidget, clearParams } =
        useAutoOpenPaymentWidgetParams();

    if (shouldOpenWidget && target) {
        return (
            <ErrorBoundary fallback={() => null}>
                <Suspense fallback={null}>
                    <WidgetOpener
                        billingFeatureName={billingFeatureName}
                        target={target}
                        onDismiss={clearParams}
                        onSuccessPurchase={onSuccessPurchase}
                    />
                </Suspense>
            </ErrorBoundary>
        );
    }
};
